import React, { useState, useEffect } from "react";
import { useUser } from "../../hooks/useUser";
import { useRoute } from "../../hooks/useRoute";
import { useAuthContext } from "../../hooks/useAuthContext";
import getAuthHeaders from "../../utils/getAuthHeaders";
import reportError from "../../utils/reportError";
import "./UserProfile.css";

const UserProfile = () => {
  const { user, setUser } = useUser();
  const { route, setRoute } = useRoute();
  const { user: authUser } = useAuthContext();

  const [editMode, setEditMode] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [location, setLocation] = useState("");
  const [permEmail, setPermEmail] = useState(false);
  const [permPhone, setPermPhone] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [zipError, setZipError] = useState("");
  const [locationError, setLocationError] = useState("");

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setPhone(user.phone || "");
      setZipCode(user.zipCode || "");
      setLocation(user.location || "");
      setPermEmail(Boolean(user.permissions && user.permissions.email));
      setPermPhone(Boolean(user.permissions && user.permissions.phone));
    }
  }, [user]);

  if (!user) {
    return (
      <div className="user-profile">
        <h2>Your Profile</h2>
        <p>No user data available.</p>
      </div>
    );
  }

  const locationOptions =
    route && route.dropLocations ? route.dropLocations.map((d) => d.name) : [];

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setStatusMessage("");

    try {
      // validate required fields (none should be empty)
      let hasError = false;
      if (!firstName || firstName.trim() === "") {
        setFirstNameError("First name is required");
        hasError = true;
      }
      if (!lastName || lastName.trim() === "") {
        setLastNameError("Last name is required");
        hasError = true;
      }
      if (!phone || phone.trim() === "") {
        setPhoneError("Phone is required");
        hasError = true;
      } else if (!(phone.length === 10 || phone.length === 11)) {
        setPhoneError("Phone must be 10 or 11 digits");
        hasError = true;
      }
      if (!zipCode || zipCode.trim() === "") {
        setZipError("Zip code is required");
        hasError = true;
      }
      if (!location || location.trim() === "") {
        setLocationError("Location is required");
        hasError = true;
      }

      if (hasError) {
        setIsSaving(false);
        return;
      }
      const authHeader = await getAuthHeaders(authUser);
      const apiUrl =
        process.env.NODE_ENV === "development"
          ? `${process.env.REACT_APP_NETWORK}/api/user/${user._id}`
          : `/api/user/${user._id}`;

      const body = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        zipCode: zipCode.trim(),
        location: location,
        permissions: { email: permEmail, phone: permPhone },
      };

      const res = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to update (${res.status})`);
      }

      const updated = await res.json();
      setUser(updated);

      // refresh route context after user profile changes (location may have changed)
      try {
        const authHeader2 = await getAuthHeaders(authUser);
        let routeUrl = `/api/routes/`;
        if (process.env.NODE_ENV === "development") {
          routeUrl = `${process.env.REACT_APP_NETWORK}${routeUrl}`;
        }
        const r = await fetch(routeUrl, { headers: authHeader2 });
        if (r.ok) {
          const routeJson = await r.json();
          setRoute(routeJson);
        }
      } catch (routeErr) {
        reportError(routeErr, "fetch route after profile save");
      }

      setStatusMessage("Saved successfully.");
      setEditMode(false);
    } catch (err) {
      reportError(err, "save profile");
      setStatusMessage(err.message || "Error saving profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // reset fields
    setFirstName(user.firstName || "");
    setLastName(user.lastName || "");
    setPhone(user.phone || "");
    setZipCode(user.zipCode || "");
    setLocation(user.location || "");
    setPermEmail(Boolean(user.permissions && user.permissions.email));
    setPermPhone(Boolean(user.permissions && user.permissions.phone));
    setEditMode(false);
    setStatusMessage("");
  };

  return (
    <div className="user-profile">
      <h2>Your Profile</h2>

      {!editMode ? (
        <div className="profile-grid">
          <div className="profile-row">
            <span className="label">Name</span>
            <span className="value">
              {user.firstName} {user.lastName}
            </span>
          </div>
          <div className="profile-row">
            <span className="label">Email</span>
            <span className="value">{user.email}</span>
          </div>
          <div className="profile-row">
            <span className="label">Phone</span>
            <span className="value">{user.phone || "-"}</span>
          </div>
          <div className="profile-row">
            <span className="label">Primary Location</span>
            <span className="value">{user.location || "-"}</span>
          </div>
          <div className="profile-row">
            <span className="label">Zip Code</span>
            <span className="value">{user.zipCode || "-"}</span>
          </div>
          <div className="profile-row">
            <span className="label">Contact Preferences</span>
          </div>
          <div className="profile-row">
            <p>
              {" "}
              I'm OK with getting a email and/or text when it's time to order:
            </p>
          </div>
          <p className="value">
            <span className="label">Email:</span>{" "}
            {user.permissions && user.permissions.email ? "Yes" : "No"}
          </p>
          <p className="value">
            <span className="label">Phone:</span>{" "}
            {user.permissions && user.permissions.phone ? "Yes" : "No"}
          </p>
          <div style={{ marginTop: 12 }}>
            <button onClick={() => setEditMode(true)}>Edit</button>
          </div>
        </div>
      ) : (
        <form className="profile-grid" onSubmit={handleSave}>
          <div className="profile-row">
            <label className="label">First Name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="profile-row">
            <label className="label">Last Name</label>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div className="profile-row">
            <label className="label">Phone</label>
            <div>
              <input
                value={phone}
                maxLength={11}
                onChange={(e) => {
                  // allow digits only, limit to 11 characters
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setPhone(digits);
                  // clear inline error while editing
                  if (phoneError) setPhoneError("");
                }}
              />
              <div
                style={{ display: "flex", alignItems: "center", marginTop: 6 }}
              >
                <small style={{ marginRight: 8, color: "#666" }}>
                  10 or 11 digits
                </small>
                {phoneError && (
                  <span className="input-error">{phoneError}</span>
                )}
              </div>
            </div>
          </div>
          <div className="profile-row">
            <label className="label">Zip Code</label>
            <input
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
            />
          </div>
          <div className="profile-row">
            <label className="label">Primary Location</label>
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            >
              {locationOptions.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>
          <label className="label">Contact Preferences</label>
          <div className="profile-row">
            <p>
              {" "}
              I'm OK with getting a email and/or text when it's time to order:
            </p>
          </div>

          <div className="profile-row">
            <label className="label">Email</label>
            <input
              type="checkbox"
              checked={permEmail}
              onChange={(e) => setPermEmail(e.target.checked)}
            />
          </div>
          <div className="profile-row">
            <label className="label">Phone</label>
            <input
              type="checkbox"
              checked={permPhone}
              onChange={(e) => setPermPhone(e.target.checked)}
            />
          </div>
          <p>
            To update your email address, please send an email to
            hello@granddadsgroceries.com.
          </p>
          <div style={{ marginTop: 12 }}>
            <button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              style={{ marginLeft: 8 }}
            >
              Cancel
            </button>
          </div>
          {statusMessage && <p>{statusMessage}</p>}
        </form>
      )}
    </div>
  );
};

export default UserProfile;
